// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@rugby/App/Store/Reducers";
import { getProductAdditionalInfos } from "@rugby/App/Store/Selectors/CustomProductDetails";
import { getDefaultImagePath } from "@rugby/App/Store/Selectors/GeneralSettings";
import LazyImage from "@rugby/MobiusOverrides/LazyImage/LazyImage";
import { color } from "@rugby/XDL/Utilities";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type categoryModel = {
    id: string;
    name: string;
    path: string;
    smallImagePath: string;
    largeImagePath: string;
    title: string;
};

const enum fields {
    title = "title",
    titleVariant = "titleVariant",
    numOfCategoriesInRow = "numOfCategoriesInRow",
    gap = "gap",
    linkColor = "linkColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleVariant]: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
        [fields.numOfCategoriesInRow]: number;
        [fields.gap]: number;
        [fields.linkColor]: string;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    productAdditionalInfos: getProductAdditionalInfos(state),
    defaultImagePath: getDefaultImagePath(state),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ComplimentaryCategoriesStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    categoryWrapper?: InjectableCss;
    clickableImage?: ClickablePresentationProps;
    linkStyle?: LinkPresentationProps;
}

export const complimentaryCategoriesStyles: ComplimentaryCategoriesStyles = {};

const styles = complimentaryCategoriesStyles;

const ComplimentaryCategories: React.FC<Props> = ({ productAdditionalInfos, defaultImagePath, fields }) => {
    const complimentaryCategory = JSON.parse(productAdditionalInfos.complimentaryCategory);

    if (!complimentaryCategory.length) {
        return null;
    }
    
    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography variant={fields.titleVariant}>{fields.title}</Typography>
            <StyledWrapper {...styles.innerWrapper} {...{ gap: fields.gap }}>
                {complimentaryCategory.map((category: categoryModel) => {
                    return (
                        <StyledWrapper
                            key={category.id}
                            {...styles.categoryWrapper}
                            {...{ numPerRow: fields.numOfCategoriesInRow, gap: fields.gap }}
                        >
                            <Clickable href={category.path} {...styles.clickableImage}>
                                <LazyImage
                                    src={category.smallImagePath || category.largeImagePath || defaultImagePath}
                                ></LazyImage>
                            </Clickable>
                            <Link href={category.path} color={fields.linkColor} {...styles.linkStyle}>
                                {category.title}
                            </Link>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ComplimentaryCategories),

    definition: {
        displayName: "Complimentary Categories",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Complimentary Categories",
                fieldType: "Translatable",
            },
            {
                name: fields.titleVariant,
                displayName: "Title Variant",
                editorTemplate: "DropDownField",
                options: [
                    {
                        value: "h1",
                        displayName: "H1",
                    },
                    {
                        value: "h2",
                        displayName: "H2",
                    },
                    {
                        value: "h3",
                        displayName: "H3",
                    },
                    {
                        value: "h4",
                        displayName: "H4",
                    },
                    {
                        value: "h5",
                        displayName: "H5",
                    },
                    {
                        value: "h6",
                        displayName: "H6",
                    },
                ],
                defaultValue: "h4",
                fieldType: "General",
            },
            {
                name: fields.numOfCategoriesInRow,
                displayName: "Categories Per Row",
                editorTemplate: "DropDownField",
                options: [
                    {
                        value: "3",
                        displayName: "3",
                    },
                    {
                        value: "4",
                        displayName: "4",
                    },
                    {
                        value: "5",
                        displayName: "5",
                    },
                    {
                        value: "6",
                        displayName: "6",
                    },
                    {
                        value: "7",
                        displayName: "7",
                    },
                    {
                        value: "8",
                        displayName: "8",
                    },
                ],
                defaultValue: "4",
                fieldType: "General",
            },
            {
                name: fields.gap,
                displayName: "Gap",
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
            },
            {
                name: fields.linkColor,
                displayName: "Link Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
